import React, { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  type AuthError,
} from 'firebase/auth';
import { useThemeColors } from '../hooks/useThemeColors';
import { useAuthContext } from '../contexts/AuthContext';
import { getFirebaseAuth } from '../utils/firebase';

/**
 * Reusable email/password account form (Firebase Authentication), extracted
 * from OnlineSyncAccount so the upgrade flow can reuse it (ADR-011). Renders
 * the form only — callers provide any card/container chrome. Success is not
 * reported via callback: AuthContext observes the auth state change and
 * consumers react to `currentUser` flipping.
 */

type AccountFormMode = 'signup' | 'signin';

interface AccountFormProps {
  /** Initial mode; the user can still toggle between them. */
  defaultMode?: AccountFormMode;
}

function friendlyError(code: string): string {
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'An account already exists for that email.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export function AccountForm({ defaultMode = 'signin' }: AccountFormProps) {
  const colors = useThemeColors();
  const { isLoading: authLoading } = useAuthContext();

  const [isSignup, setIsSignup] = useState(defaultMode === 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const auth = getFirebaseAuth();
      if (isSignup) {
        await createUserWithEmailAndPassword(auth, email.trim(), password);
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
      setPassword('');
      // onAuthStateChanged (AuthContext) notifies consumers of the new user.
    } catch (err) {
      setError(friendlyError((err as AuthError)?.code ?? ''));
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    backgroundColor: colors.settings.inputBackground,
    borderColor: colors.settings.inputBorder,
    color: colors.settings.heading,
  };

  return (
    <div className="flex flex-col space-y-4">
      <h3 className="text-lg font-medium text-center" style={{ color: colors.settings.heading }}>
        {isSignup ? 'Create an account' : 'Sign in to sync your prompts'}
      </h3>
      <p className="text-sm text-center text-gray-600 dark:text-gray-400">
        Back up your library and sync it across devices.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col space-y-3">
        <input
          type="email"
          required
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          style={inputStyle}
        />
        <input
          type="password"
          required
          autoComplete={isSignup ? 'new-password' : 'current-password'}
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          className="w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          style={inputStyle}
        />

        {error ? <p className="text-sm text-red-500">{error}</p> : null}

        <button
          type="submit"
          disabled={busy || authLoading}
          className="px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-60"
        >
          {busy ? 'Please wait…' : isSignup ? 'Create account' : 'Sign in'}
        </button>
      </form>

      <button
        type="button"
        onClick={() => {
          setError('');
          setIsSignup(v => !v);
        }}
        className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
      >
        {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
      </button>
    </div>
  );
}
